import React, { useCallback, useEffect, useState } from 'react';
import { Zap, CheckCircle2, AlertTriangle, PauseCircle, Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSSEEvent } from '../../contexts/SSEContext';
import { SSE_EVENTS } from '../../services/global/sseService';
import SwipeToast from '../notifications/SwipeToast';
import CampaignResultModal from './CampaignResultModal';

/**
 * Toast global ancorado no canto superior direito que mostra progresso ao
 * vivo de campanhas via SSE. Aparece automaticamente quando o backend
 * emite CAMPAIGN_STARTED e some apos o usuario fechar manualmente ou abrir
 * o modal de resultado.
 *
 * Estado interno e simples — um unico run "ativo" por vez. Em multi-campanha
 * concorrente, a mais recente sobrescreve o slot (caso comum: usuario dispara
 * uma de cada vez e fica acompanhando essa).
 */
const STATUS_PAUSED = 'paused';
// Motivo da pausa (paused_reason do run). Sem motivo conhecido cai no texto
// antigo do teto diário.
export const PAUSE_REASON_TEXT = {
  teto_diario: 'Limite diário atingido. Retoma automaticamente.',
  sessao_fechada: 'O WhatsApp está desconectado. Reconecte pelo QR Code para a campanha continuar.',
  meta_rate_limit: 'A Meta pediu para desacelerar. Retoma automaticamente em alguns minutos.',
  meta_tier: 'Limite diário da Meta para este número atingido. Retoma no dia seguinte.',
  ru: 'Banco de dados saturado. Retoma automaticamente.',
  leitura: 'Falha temporária de leitura. Retoma automaticamente.',
};
const STATUS_COMPLETED = 'completed';
const STATUS_FAILED = 'failed';

const CampaignProgressToast = () => {
  const { colors } = useTheme();
  const [run, setRun] = useState(null);
  const [visible, setVisible] = useState(false);
  const [resultModalRunId, setResultModalRunId] = useState(null);

  const handleEvent = useCallback((data) => {
    if (!data || !data.campaign_run_id) return;
    setRun(data);
    setVisible(true);
  }, []);

  useSSEEvent(SSE_EVENTS.CAMPAIGN_STARTED, handleEvent, []);
  useSSEEvent(SSE_EVENTS.CAMPAIGN_PROGRESS, handleEvent, []);
  useSSEEvent(SSE_EVENTS.CAMPAIGN_PAUSED, handleEvent, []);
  useSSEEvent(SSE_EVENTS.CAMPAIGN_COMPLETED, handleEvent, []);
  useSSEEvent(SSE_EVENTS.CAMPAIGN_FAILED, handleEvent, []);

  // Auto-dismiss completed/failed apos 90s se o usuario nao interagir, para
  // o toast nao ficar pendurado eternamente.
  useEffect(() => {
    if (!run) return;
    if (run.status !== STATUS_COMPLETED && run.status !== STATUS_FAILED) return;
    const t = window.setTimeout(() => setVisible(false), 90000);
    return () => window.clearTimeout(t);
  }, [run]);

  const dismiss = useCallback(() => setVisible(false), []);

  if (!run) return null;

  // Mapeamento status -> visual.
  const status = run.status;
  const sent = run.sent_count || 0;
  const failed = run.failed_count || 0;
  const skipped = run.skipped_count || 0;
  const total = run.total_recipients || 0;
  const processed = sent + failed + skipped;
  const percent = run.percent != null ? run.percent : (total ? Math.round((processed * 100) / total) : 0);

  let icon = <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />;
  let accent = colors.accentPrimary;
  let title = 'Campanha em andamento';
  let description = `${processed} de ${total} processados (${percent}%)`;
  let ctaLabel = 'Ver detalhe';

  if (status === STATUS_COMPLETED) {
    icon = <CheckCircle2 size={20} />;
    accent = '#22c55e';
    title = 'Campanha concluída';
    description = `${sent} enviados, ${failed} falhas, ${skipped} pulados`;
    ctaLabel = 'Ver resultado';
  } else if (status === STATUS_PAUSED) {
    icon = <PauseCircle size={20} />;
    accent = '#f59e0b';
    title = 'Campanha pausada';
    description = PAUSE_REASON_TEXT[run.paused_reason] || `Limite diário atingido. Retoma automaticamente.`;
    if (run.paused_reason === 'sessao_fechada') accent = '#ea4335';
    ctaLabel = 'Ver detalhe';
  } else if (status === STATUS_FAILED) {
    icon = <AlertTriangle size={20} />;
    accent = '#ea4335';
    title = 'Campanha com falha';
    description = `Algo deu errado. Veja o detalhe para reenviar.`;
    ctaLabel = 'Ver detalhe';
  } else {
    icon = <Zap size={20} />;
  }

  return (
    <>
      <SwipeToast
        isOpen={visible}
        icon={icon}
        title={title}
        description={description}
        ctaLabel={ctaLabel}
        onCta={() => {
          setResultModalRunId(run.campaign_run_id);
          setVisible(false);
        }}
        onDismiss={dismiss}
        accentColor={accent}
      />
      {resultModalRunId && (
        <CampaignResultModal
          runId={resultModalRunId}
          onClose={() => setResultModalRunId(null)}
        />
      )}
    </>
  );
};

export default CampaignProgressToast;
